<template>
  <div class="message-list">
    <div class="columns">
      <div class="column col-4 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.filter.startDate.$error
                || $v.filter.endDate.$error}">
        <label class="form-label">Período</label>
        <div class="input-group">
          <dx-dropdown
            class="input-group-addon"
            :items="periodOptions"
            label="text"
            @select="setPeriod"
            direction="left"
          ><fa-icon :icon="['fal', 'chevron-down']" /></dx-dropdown>
          <dx-input-date
            id="start-date"
            class="form-input text-center"
            v-model="filter.startDate"
            @change="changedStartDate"
            @blur="$v.filter.startDate.$touch()"
          />
          <dx-input-date
            id="end-date"
            class="form-input text-center"
            v-model="filter.endDate"
            @change="changedEndDate"
            @blur="$v.filter.endDate.$touch()"
          />
        </div>
      </div>
      <div class="column col-md-6 col-sm-12 form-group">
        <label for="type" class="form-label">Tipo</label>
        <select id="type" class="form-select" v-model="filter.type">
          <option value="">Todos os tipos</option>
          <option
            v-for="(text, value) in types"
            :value="value"
            :key="value"
          >{{ text }}</option>
        </select>
      </div>
      <div class="column col-md-6 col-sm-12 form-group">
        <label for="channel" class="form-label">Canal</label>
        <select id="channel" class="form-select" v-model="filter.channel">
          <option value="">Todos os canais</option>
          <option
            v-for="(text, value) in channels"
            :value="value"
            :key="value"
          >{{ text }}</option>
        </select>
      </div>
      <div class="column form-group">
        <label for="status" class="form-label">Status</label>
        <select id="status" class="form-select" v-model="filter.status">
          <option value="">Todos os status</option>
          <option
            v-for="(text, value) in statuses"
            :value="value"
            :key="value"
          >{{ text }}</option>
        </select>
      </div>
      <div class="column col-auto">
        <label class="form-label">&nbsp;</label>
        <button
          class="btn btn-primary btn-icon btn-icon-left"
          type="button"
          @click="search"
        ><fa-icon :icon="['fal', 'search']"/> Filtrar</button>
      </div>
    </div>

    <div class="loading mt-2" v-if="loading"></div>
    <template v-else>
      <template v-if="data.items.length === 0">
        <div class="empty mt-2">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
          </div>
          <p class="empty-title h5">Mensagens</p>
          <p class="empty-subtitle">
            Nenhuma mensagem encontrado. Verifique os filtros para realizar novas buscas
          </p>
        </div>
      </template>
      <template v-else>
        <table class="table table-striped table-hover">
          <thead>
          <tr>
            <th class="hide-sm" style="width:40px">#</th>
            <th>Data</th>
            <th>Tipo</th>
            <th>Contato</th>
            <th>Canal</th>
            <th>Status</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>{{ item.date | date('datetime') }}</td>
              <td>{{ item.typeName }}</td>
              <td>{{ item.contact.value }} - {{ item.contact.name }}</td>
              <td>{{ item.channelName }}</td>
              <td>
                <span
                  class="label label-rounded"
                  :class="item.statusLabelClass"
                >{{ item.statusName }}</span>
              </td>
              <td class="text-right">
                <button
                  @click="showDetails(item)"
                  class="btn btn-sm btn-action btn-icon btn-gray tooltip"
                  data-tooltip="Detalhar"
                ><fa-icon :icon="['fal', 'eye']"></fa-icon></button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </template>

    <div class="text-center pt-2 mt-2" v-if="data.hasMore && !loading">
      <button
        class="btn btn-primary btn-fixed-width"
        @click="loadMore"
        :disabled="loadingMore"
        :class="{ loading: loadingMore }"
      >Carregar mais</button>
    </div>

    <dx-modal
      v-if="modalDetails.show"
      v-model="modalDetails.show"
      title="Detalhe da mensagem"
      id="message-detail"
    >
      <div class="mb-1">
        Contato:
        <strong class="text-info">
          {{ modalDetails.data.contact.value }}
          - {{ modalDetails.data.contact.name }}
        </strong>
      </div>

      <message-preview
        :channel="modalDetails.data.channel"
        :header="modalDetails.data.header"
        :body="modalDetails.data.body"
        :footer="modalDetails.data.footer"
      />

      <div class="columns mb-1">
        <div class="column col-6">
          Tipo: <strong>{{ modalDetails.data.typeName }}</strong>
        </div>
        <div class="column col-6">
          Canal: <strong>{{ modalDetails.data.channelName }}</strong>
        </div>
        <div class="column col-6 pt-2">
          Data: <strong>{{ modalDetails.data.date | date('datetime') }}</strong>
        </div>
        <div class="column col-6 pt-2">
          Status: <strong>{{ modalDetails.data.statusName }}</strong>
        </div>
      </div>
    </dx-modal>
  </div>
</template>

<script>
import moment from 'moment';
import MessagePreview from '@/components/message/Preview.vue';
import { types, getName as getTypeName } from '@/data/message-types';
import { channels, getName as getChannelName } from '@/data/message-channels';
import { statuses, getName as getStatusName } from '@/data/message-statuses';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import { messageStatusLabelClass as statusLabelClass } from './helpers';

export default {
  components: {
    MessagePreview,
  },
  data() {
    return {
      debounceTimeout: null,
      loading: false,
      period: 'month',
      periodOptions: [],
      filter: {
        type: '',
        channel: '',
        category: '',
        status: '',
        startDate: '',
        endDate: '',
      },
      loadingMore: false,
      data: {
        items: [],
        total: 0,
        limit: 200,
        offset: 0,
        hasMore: false,
      },
      modalDetails: {
        show: false,
        data: null,
      },
      types,
      channels,
      statuses,
    };
  },
  mounted() {
    this.init();
  },
  validations() {
    const rules = {
      filter: {
        startDate: { required, date },
        endDate: { required, date },
      },
    };

    if (this.filter.startDate && moment(this.filter.startDate).isValid()) {
      rules.filter.endDate = {
        required,
        date,
        minDate: minDate(this.filter.startDate),
      };
    }

    return rules;
  },
  methods: {
    init() {
      this.loadPeriod();
      this.changedStartDate();
      this.load();
    },
    loadPeriod() {
      const now = new Date();
      for (let i = 0; i < 6; i += 1) {
        this.periodOptions.push(
          {
            value: i,
            text: moment(now).subtract(i, 'month').format('MM/YYYY'),
          },
        );
      }
    },
    changedStartDate() {
      if (!this.filter.startDate) {
        this.filter.startDate = moment()
          .startOf('month')
          .format('YYYY-MM-DD');
        this.filter.endDate = moment()
          .endOf('month')
          .format('YYYY-MM-DD');
      }
    },
    changedEndDate() {
      if (!this.filter.startDate && !this.filter.endDate) {
        this.filter.startDate = moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        this.filter.endDate = moment()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD');
      }
      if (!this.filter.endDate) {
        this.filter.endDate = moment(this.filter.startDate)
          .endOf('month')
          .format('YYYY-MM-DD');
      }
    },
    setPeriod({ value }) {
      this.filter.startDate = moment()
        .subtract(value, 'month')
        .startOf('month')
        .format('YYYY-MM-DD');
      this.filter.endDate = moment()
        .subtract(value, 'month')
        .endOf('month')
        .format('YYYY-MM-DD');
    },
    search() {
      this.data.offset = 0;
      this.load();
    },
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.load();
    },
    async load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) {
        return;
      }

      const duration = moment.duration(moment(this.filter.endDate)
        .diff(moment(this.filter.startDate))).asMonths();

      if (duration > 6) {
        this.$toast.show('O intervalo entre as datas não pode ser superior a 6 meses!',
          { type: 'error' });
        return;
      }

      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      Object.keys(this.filter).forEach((key) => {
        if (this.filter[key]) {
          params[key] = this.filter[key];
        }
      });

      try {
        const { data } = await this.$http.get('/messages', { params });

        data.items = data.items.map((item) => {
          item.channelName = getChannelName(item.channel);
          item.typeName = getTypeName(item.type);
          item.statusName = getStatusName(item.status);
          item.statusLabelClass = statusLabelClass(item.status);
          return item;
        });

        this.data.hasMore = data.hasMore;
        this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
      } catch (e) {
        this.$toast.error(e);
      }

      this.loading = false;
      this.loadingMore = false;
    },
    showDetails(data) {
      this.modalDetails.show = true;
      this.modalDetails.data = data;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

#message-detail {
  strong {
    font-weight: 600;
  }
  .message-preview {
    margin: $layout-spacing-lg 0;
  }
}
</style>
